/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ActionCondition from 'cio/app-models/conditions/action';
import { action, computed } from '@ember/object';

const INVERSE_OPTIONS_FOREIGN_EVENT = Object.freeze([
  { value: 'foreign_event', label: 'is', disabled: false },
  { value: 'false', label: 'has ever', disabled: false },
  { value: 'true', label: 'has not ever', disabled: false },
]);

const INVERSE_OPTIONS = Object.freeze([
  { value: 'false', label: 'has ever', disabled: false },
  { value: 'true', label: 'has not ever', disabled: false },
]);

interface Args {
  condition: ActionCondition;
  disabled: boolean;
  size: string;
  variation: string;
  fluid: boolean;
  allowForeignEvent?: boolean;
}

export default class Inverse extends Component<Args> {
  initialValue = this.selectedValue;

  get allowForeignEvent() {
    return this.args.allowForeignEvent ?? false;
  }

  get inverseOptions() {
    if (this.allowForeignEvent) {
      let options = INVERSE_OPTIONS_FOREIGN_EVENT;

      // Remove the "true" option if it's not used, as it's not actually very
      // useful to have.
      // See ConditionEditor::Event::Inverse for a similar case
      if (this.initialValue !== 'true') {
        options = options.filter((option) => option.value !== 'true');
      } else {
        options = options.map((option) => {
          // Disable the "true" option so it can't be re-selected
          if (option.value === 'true') {
            return { ...option, disabled: true };
          }

          return option;
        });
      }

      return options;
    }

    let options = [...INVERSE_OPTIONS];

    // If the initial value is "foreign_event", add the foreign event option as
    // a fallback. This should only happen if a customer has already saved a
    // "foreign event" action condition, which we're now disabling.
    if (this.initialValue === 'foreign_event') {
      options.unshift({ ...INVERSE_OPTIONS_FOREIGN_EVENT[0], disabled: true });
    }

    return options;
  }

  @computed('args.condition.{isForeignEvent,inverse}')
  get selectedValue() {
    let { isForeignEvent, inverse } = this.args.condition;
    if (isForeignEvent ?? false) {
      return 'foreign_event';
    }

    return String(inverse);
  }

  @action
  onChange(value: 'foreign_event' | 'true' | 'false') {
    let { condition } = this.args;

    if ((condition.isForeignEvent ?? false) && !this.allowForeignEvent && value !== 'foreign_event') {
      condition.setProperties({ isForeignEvent: undefined, inverse: value === 'true' });
      return;
    }

    if (this.allowForeignEvent) {
      condition.setProperties({ isForeignEvent: value === 'foreign_event', inverse: value === 'true' });
      return;
    }

    condition.setProperties({ inverse: value === 'true' });
  }
}
